import { useState, useEffect, useRef } from 'react';
import { useScope } from '../lib/ScopeContext.jsx';
import { ANY_VALUE, ANY_VALUE_LABEL } from '../lib/filterConstants.js';
import { PRESETS, presetExpr, parseDateValue, rangeToExpr, describeDateValue, formatDay, todayIso } from '../lib/dateFilter.mjs';
import DateRangeCalendar from './DateRangeCalendar.jsx';

// ─── The dashboard date filter's control ──────────────────────────────────────
// A start + end date range in two taps, with the presets people actually ask for
// — replacing the value dropdown a date filter used to fall through to, which
// listed individual dates oldest-first (so only 2014-2015 were reachable) and
// made a range a tick-box exercise.
//
// MOBILE-FIRST: one column that stacks, native <input type="date"> (so a phone
// opens its own date wheel rather than a cramped custom calendar), every tap
// target ≥ 40px, and nothing with a fixed width. The desktop filter panel lays
// these out in its flex row; the mobile sheet already stacks them.
//
// The VALUE is always a Looker date expression (client/src/lib/dateFilter.mjs
// owns that codec) so it travels through the existing filter plumbing into the
// tile queries untouched — tiles update exactly as they do for any other filter.

// The data's own date window, per (model, explore, field). Cached for the
// session: a dashboard can carry the same date filter on several controls, and
// this is a Looker round-trip.
const windowCache = new Map(); // key -> { min, max } | null
const inflight = new Map();

function useDataWindow({ model, explore, field, suiteId, enabled }) {
  const key = enabled && model && explore && field ? `${model}|${explore}|${field}|${suiteId || ''}` : '';
  const [win, setWin] = useState(() => (key ? windowCache.get(key) || null : null));
  useEffect(() => {
    if (!key) { setWin(null); return; }
    if (windowCache.has(key)) { setWin(windowCache.get(key)); return; }
    let alive = true;
    let p = inflight.get(key);
    if (!p) {
      p = fetch('/api/filter-date-range', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ model, explore, field, suiteId }),
      })
        .then((r) => (r.ok ? r.json() : {}))
        .then((d) => {
          const w = d && (d.min || d.max) ? { min: d.min || '', max: d.max || '' } : null;
          windowCache.set(key, w);
          return w;
        })
        .catch(() => { windowCache.set(key, null); return null; })   // fails soft: no bounds, still pick any date
        .finally(() => inflight.delete(key));
      inflight.set(key, p);
    }
    p.then((w) => { if (alive) setWin(w || null); });
    return () => { alive = false; };
  }, [key, model, explore, field, suiteId]);
  return win;
}

export default function DateRangeFilter({ filter, value, onChange }) {
  const { suiteId } = useScope();
  // The "any value" sentinel is a filter-wide thing, not a date expression: it
  // drops the field from the tile query altogether, which for a date filter is
  // the only way to see EVERY date on a tile that carries its own baked one
  // (blank just falls back to that default). Keep it out of the codec.
  const isAny = value === ANY_VALUE;
  const parsed = isAny ? parseDateValue('') : parseDateValue(value);
  const field = filter.field || filter.dimension;
  // Only ask Looker for the window once the picker is actually in use.
  const [asked, setAsked] = useState(false);
  const dataWindow = useDataWindow({ model: filter.model, explore: filter.explore, field, suiteId, enabled: asked });
  // "Custom" opens the two date inputs; an unrecognised expression opens the
  // text box instead, so a hand-written Looker expression stays editable.
  const [custom, setCustom] = useState(parsed.mode === 'range');
  const [expr, setExpr] = useState(parsed.mode === 'expr');
  const lastValue = useRef(value);

  // Follow the value when something else changes it (a preset tap, a reset to
  // the client default, a saved view loading).
  useEffect(() => {
    if (lastValue.current === value) return;
    lastValue.current = value;
    if (value === ANY_VALUE) { setCustom(false); setExpr(false); return; }
    const p = parseDateValue(value);
    if (p.mode === 'range') { setCustom(true); setExpr(false); }
    else if (p.mode === 'expr') { setExpr(true); setCustom(false); }
  }, [value]);

  const open = () => { if (!asked) setAsked(true); };
  const write = (v) => { lastValue.current = v; onChange(v); };
  const pickPreset = (key) => { setCustom(false); setExpr(false); write(presetExpr(key)); };
  const setStart = (d) => write(rangeToExpr(d, parsed.end));
  const setEnd = (d) => write(rangeToExpr(parsed.start, d));

  // The calendar's bounds: never narrower than the data, never so narrow that
  // today is unreachable (the whole complaint was recent dates being missing).
  const today = todayIso();
  const min = dataWindow?.min || undefined;
  const max = dataWindow?.max && dataWindow.max > today ? dataWindow.max : (dataWindow?.min ? today : undefined);

  return (
    <div style={wrap} onFocus={open} onPointerDown={open}>
      <div style={chipRow}>
        <Chip active={isAny} onClick={() => { setCustom(false); setExpr(false); write(ANY_VALUE); }} title="Every date — no restriction at all, even on a tile that carries its own date filter">All dates</Chip>
        {PRESETS.map((p) => (
          <Chip key={p.key} active={!isAny && parsed.mode === 'preset' && parsed.preset === p.key} onClick={() => pickPreset(p.key)} title={p.hint ? `${p.label} — ${p.hint}` : p.label}>{p.label}</Chip>
        ))}
        <Chip active={!isAny && custom} onClick={() => { setExpr(false); setCustom((v) => !v); open(); }} title="Pick a start and an end date on a calendar">📅 Pick dates</Chip>
      </div>

      {!isAny && custom && (
        <>
          {/* The calendar IS the picker — two taps, no typing. The two boxes
              under it are the typed shortcut for anyone who prefers it. */}
          <DateRangeCalendar
            start={parsed.start}
            end={parsed.end}
            min={min}
            max={max}
            onPick={(s, e) => write(rangeToExpr(s, e))}
          />
          <div style={rangeRow}>
            <label style={dateField}>
              <span style={smallLabel}>Start</span>
              <input type="date" value={parsed.start} min={min} max={max} onChange={(e) => setStart(e.target.value)} style={dateInput} aria-label={`${filter.title} start date`} />
            </label>
            <label style={dateField}>
              <span style={smallLabel}>End</span>
              <input type="date" value={parsed.end} min={parsed.start || min} max={max} onChange={(e) => setEnd(e.target.value)} style={dateInput} aria-label={`${filter.title} end date`} />
            </label>
          </div>
        </>
      )}

      {!isAny && expr && (
        <input
          type="text"
          value={parsed.raw}
          onChange={(e) => write(e.target.value)}
          placeholder="e.g. 7 days ago for now"
          style={exprInput}
          title="Looker date filter expression"
          aria-label={`${filter.title} date expression`}
        />
      )}

      <div style={footRow}>
        <span style={summary} title={isAny ? 'Every date — the field is left out of the query' : (parsed.raw || 'Whatever each tile filters to by default')}>
          {isAny ? ANY_VALUE_LABEL : (parsed.mode === 'all' ? (filter.default_value ? 'Report default' : 'No date set') : describeDateValue(value))}
        </span>
        {value !== '' && <button type="button" style={linkAction} onClick={() => { setCustom(false); setExpr(false); write(''); }} title="Back to what this report filters to by default">Clear</button>}
        {!isAny && custom && dataWindow && (dataWindow.min || dataWindow.max) && (
          <button
            type="button"
            style={linkAction}
            onClick={() => write(rangeToExpr(dataWindow.min, dataWindow.max))}
            title={`Everything the data holds: ${formatDay(dataWindow.min) || '…'} to ${formatDay(dataWindow.max) || '…'}`}
          >
            Full data range
          </button>
        )}
        {!isAny && (expr
          ? <button type="button" style={linkAction} onClick={() => setExpr(false)}>Use the picker</button>
          : <button type="button" style={linkAction} onClick={() => { setExpr(true); setCustom(false); }} title="Type a Looker date expression, e.g. “7 days ago for now”">Expression…</button>)}
      </div>

      {!isAny && custom && dataWindow && (dataWindow.min || dataWindow.max) && (
        <span style={hint}>Data available {formatDay(dataWindow.min) || '…'} → {formatDay(dataWindow.max) || '…'}</span>
      )}
    </div>
  );
}

function Chip({ active, onClick, title, children }) {
  return (
    <button type="button" onClick={onClick} title={title} style={{ ...chip, ...(active ? chipOn : null) }}>{children}</button>
  );
}

// Mobile-first: the wrapper fills the sheet's single column; on the desktop
// filter row it takes a sensible share without ever being pinned to a width.
const wrap = { display: 'flex', flexDirection: 'column', gap: 8, minWidth: 0, width: '100%' };
const chipRow = { display: 'flex', flexWrap: 'wrap', gap: 6 };
const chip = { minHeight: 40, padding: '9px 13px', borderRadius: 980, border: '1px solid var(--hairline)', background: 'var(--card)', color: 'var(--text)', fontSize: 12.5, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' };
const chipOn = { background: 'var(--brand)', borderColor: 'var(--brand)', color: '#fff' };
const rangeRow = { display: 'flex', flexWrap: 'wrap', gap: 8 };
const dateField = { display: 'flex', flexDirection: 'column', gap: 4, flex: '1 1 140px', minWidth: 0 };
const smallLabel = { fontSize: 10.5, fontWeight: 600, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.06em' };
const dateInput = { minHeight: 44, padding: '9px 12px', border: '1px solid var(--hairline)', borderRadius: 10, fontSize: 13, outline: 'none', width: '100%', background: 'var(--card)', color: 'var(--text)', boxSizing: 'border-box' };
const exprInput = { ...dateInput };
const footRow = { display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8 };
const summary = { flex: '1 1 auto', fontSize: 12.5, fontWeight: 600, color: 'var(--text)', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };
const linkAction = { minHeight: 32, padding: '4px 8px', background: 'transparent', border: 'none', color: 'var(--brand)', fontSize: 12, fontWeight: 600, cursor: 'pointer' };
const hint = { fontSize: 11.5, color: 'var(--muted)' };
